import type { PerformancePart } from '@tunasong/schemas'
import { normalizeParts, performancePartLabels, performanceParts } from '@tunasong/schemas'
import { Checkbox, FormControlLabel, FormGroup, Typography, useUserConfig } from '@tunasong/ui-lib'
import { useId, type FC } from 'react'
import styles from './gear-config.module.css'

/**
 * Toggle a part in the profile default. Unlike the Perform part menu, the default may be emptied:
 * no default means a band asks with the picker, so the result is `undefined` rather than Full chart.
 * Full chart is exclusive in both directions.
 */
const toggleDefaultPart = (current: readonly PerformancePart[], part: PerformancePart) => {
  if (part === 'full') {
    return current.includes('full') ? undefined : (['full'] satisfies PerformancePart[])
  }
  const others = current.filter(p => p !== 'full')
  return normalizeParts(others.includes(part) ? others.filter(p => p !== part) : [...others, part])
}

/**
 * Profile → Gear: the parts this person plays by default. Used for personal songs and for every band
 * where they have not picked parts. It is never copied into a band.
 */
export const DefaultPartsConfig: FC = () => {
  const [performance, updatePerformance] = useUserConfig('performance')
  const selected = normalizeParts(performance?.defaultParts) ?? []
  const titleId = useId()
  const helperId = useId()

  const handleToggle = (part: PerformancePart) => () =>
    updatePerformance({ ...performance, defaultParts: toggleDefaultPart(selected, part) })

  return (
    <div className={styles.formField}>
      <Typography id={titleId} variant="h6" className={styles.sectionTitle}>
        Default parts
      </Typography>
      <Typography id={helperId} variant="body2" color="textSecondary" className={styles.description}>
        Used for new bands and your own songs. Each band can override them.
      </Typography>
      <FormGroup role="group" aria-labelledby={titleId} aria-describedby={helperId}>
        {performanceParts.map(part => (
          <FormControlLabel
            key={part}
            control={<Checkbox checked={selected.includes(part)} onCheckedChange={handleToggle(part)} />}
            label={performancePartLabels[part]}
          />
        ))}
      </FormGroup>
    </div>
  )
}

export default DefaultPartsConfig
